import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';
import { formatMoney } from './money-formatting.js';

class FpSizeQuantity extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready === 'true') return;
    this.dataset.ready = 'true';

    this.rows = Array.from(this.querySelectorAll('[data-size-row]'));
    this.addButtons = Array.from(this.querySelectorAll('[data-add-selected]'));
    this.buyButtons = Array.from(this.querySelectorAll('[data-buy-selected]'));
    this.addButtonTexts = Array.from(this.querySelectorAll('[data-add-button-text]'));
    this.buyButtonTexts = Array.from(this.querySelectorAll('[data-buy-button-text]'));
    this.message = this.querySelector('[data-size-message]');
    this.totalItemsElement = this.querySelector('[data-total-items]');
    this.totalItemsLabel = this.querySelector('[data-total-items-label]');
    this.totalPriceElement = this.querySelector('[data-total-price]');
    this.defaultAddLabel = this.dataset.addLabel || 'Add to Cart';
    this.defaultBuyLabel = this.buyButtonTexts[0]?.textContent?.trim() || 'Buy Now';

    for (const row of this.rows) {
      const minus = row.querySelector('[data-quantity-minus]');
      const plus = row.querySelector('[data-quantity-plus]');
      const input = row.querySelector('[data-quantity-input]');

      minus?.addEventListener('click', () => this.changeQuantity(row, -1));
      plus?.addEventListener('click', () => this.changeQuantity(row, 1));
      input?.addEventListener('change', () => this.normalizeInput(row));
      input?.addEventListener('input', () => this.update());
    }

    this.addButtons.forEach((button) => button.addEventListener('click', () => this.addSelected(false)));
    this.buyButtons.forEach((button) => button.addEventListener('click', () => this.addSelected(true)));
    this.setupSizeGuide();
    this.update();
  }

  setupSizeGuide() {
    const proxy = this.querySelector('[data-size-guide-proxy]');
    const productDetails = this.closest('.product-details');
    const original = productDetails?.querySelector('fp-size-guide [data-size-guide-open]');

    if (!proxy || !original) return;

    original.hidden = true;
    proxy.hidden = false;
    proxy.addEventListener('click', () => original.click());
  }

  getQuantity(row) {
    const input = row.querySelector('[data-quantity-input]');
    return input ? Math.max(0, Number.parseInt(input.value, 10) || 0) : 0;
  }

  normalizeInput(row) {
    const input = row.querySelector('[data-quantity-input]');
    if (!input) return;

    const minimum = Number.parseInt(row.dataset.min || '1', 10);
    const step = Number.parseInt(row.dataset.step || '1', 10);
    const maximum = row.dataset.max ? Number.parseInt(row.dataset.max, 10) : Number.POSITIVE_INFINITY;
    let value = Math.max(0, Number.parseInt(input.value, 10) || 0);

    if (value > 0) {
      value = Math.max(minimum, value);
      value = minimum + Math.round((value - minimum) / step) * step;
      value = Math.min(maximum, value);
    }

    input.value = String(value);
    this.update();
  }

  changeQuantity(row, direction) {
    const input = row.querySelector('[data-quantity-input]');
    if (!input) return;

    const current = this.getQuantity(row);
    const minimum = Number.parseInt(row.dataset.min || '1', 10);
    const step = Number.parseInt(row.dataset.step || '1', 10);
    const maximum = row.dataset.max ? Number.parseInt(row.dataset.max, 10) : Number.POSITIVE_INFINITY;
    let next;

    if (direction > 0) {
      next = current === 0 ? minimum : Math.min(maximum, current + step);
    } else {
      next = current <= minimum ? 0 : Math.max(minimum, current - step);
    }

    input.value = String(next);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  getEasifyProperties() {
    const container = this.closest('.product-details') || document;
    const seen = new Set();
    const properties = {};
    const collect = (input) => {
      const name = input.getAttribute('name');
      if (!name || !/^properties\[(.+)\]$/.test(name) || seen.has(name)) return;
      if (input.type === 'checkbox' && !input.checked) return;
      const value = input.value;
      if (value === '' || value === null || value === undefined) return;
      seen.add(name);
      properties[name.replace(/^properties\[(.+)\]$/, '$1')] = value;
    };
    const form = this.querySelector('[data-easify-form]');
    if (form) form.querySelectorAll('input, select, textarea').forEach(collect);
    container.querySelectorAll('input, select, textarea').forEach(collect);
    return properties;
  }
  selectedItems() {
    const properties = this.getEasifyProperties();
    const hasProperties = Object.keys(properties).length > 0;
    return this.rows
      .map((row) => {
        const item = {
          id: Number(row.dataset.variantId),
          quantity: this.getQuantity(row),
        };
        if (hasProperties) item.properties = properties;
        return item;
      })
      .filter((item) => item.id && item.quantity > 0);
  }

  totalQuantity() {
    return this.selectedItems().reduce((sum, item) => sum + item.quantity, 0);
  }

  totalPrice() {
    return this.rows.reduce((sum, row) => {
      const price = Number.parseInt(row.dataset.price || '0', 10) || 0;
      return sum + price * this.getQuantity(row);
    }, 0);
  }

  update() {
    const total = this.totalQuantity();
    const totalPrice = this.totalPrice();

    for (const row of this.rows) {
      const quantity = this.getQuantity(row);
      row.classList.toggle('is-selected', quantity > 0);

      const minus = row.querySelector('[data-quantity-minus]');
      const plus = row.querySelector('[data-quantity-plus]');
      const maximum = row.dataset.max ? Number.parseInt(row.dataset.max, 10) : Number.POSITIVE_INFINITY;

      if (minus) minus.disabled = quantity <= 0;
      if (plus) plus.disabled = quantity >= maximum;
    }

    [...this.addButtons, ...this.buyButtons].forEach((button) => {
      button.setAttribute('aria-disabled', total > 0 ? 'false' : 'true');
    });

    this.addButtonTexts.forEach((text) => {
      text.textContent = total > 0 ? `${this.defaultAddLabel} · ${total}` : this.defaultAddLabel;
    });
    this.buyButtonTexts.forEach((text) => {
      text.textContent = total > 0 ? `${this.defaultBuyLabel} · ${total}` : this.defaultBuyLabel;
    });

    if (this.totalItemsElement) this.totalItemsElement.textContent = String(total);
    if (this.totalItemsLabel) this.totalItemsLabel.textContent = total === 1 ? 'item' : 'items';
    if (this.totalPriceElement) {
      const currency = this.dataset.currency || window.Shopify?.currency?.active || 'USD';
      const moneyFormat = this.dataset.moneyFormat || '${{amount}}';
      this.totalPriceElement.textContent = formatMoney(totalPrice, moneyFormat, currency);
    }

    if (this.message?.classList.contains('is-error')) {
      this.message.textContent = '';
      this.message.classList.remove('is-error');
    }
  }

  promptForSelection() {
    this.querySelector('.fp-size-quantity__header')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (this.message) {
      this.message.textContent = 'Choose a size and quantity first.';
      this.message.classList.add('is-error');
    }
  }

  setBusy(isBusy, checkout = false) {
    [...this.addButtons, ...this.buyButtons].forEach((button) => {
      button.disabled = isBusy;
      button.toggleAttribute('aria-busy', isBusy);
    });
    if (!isBusy) return;

    const loadingText = checkout ? 'Preparing checkout…' : 'Adding…';
    (checkout ? this.buyButtonTexts : this.addButtonTexts).forEach((text) => {
      text.textContent = loadingText;
    });
  }

  async addSelected(checkout) {
    const items = this.selectedItems();
    if (items.length === 0) {
      this.promptForSelection();
      return;
    }

    const cartComponents = Array.from(document.querySelectorAll('cart-items-component'));
    const sectionIds = cartComponents.map((component) => component.dataset.sectionId).filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();
    const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);

    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((item) => ({
        merchandiseId: String(item.id),
        quantity: item.quantity,
      })),
      promise: deferred.promise,
    }));

    this.setBusy(true, checkout);
    if (this.message) {
      this.message.textContent = '';
      this.message.classList.remove('is-error');
    }

    try {
      const addResponse = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items,
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const addData = await addResponse.json();

      if (!addResponse.ok || addData.status) {
        throw new Error(addData.description || addData.message || 'Could not add the selected sizes.');
      }

      const cartResponse = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
      });
      if (!cartResponse.ok) throw new Error('Could not refresh the cart.');
      const cart = await cartResponse.json();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'fp-size-quantity',
          sourceId: this.id,
          itemCount: totalQuantity,
          productId: this.dataset.productId,
          sections: addData.sections,
          didError: false,
        },
      });

      if (checkout) {
        const root = Theme.routes.root_url || '/';
        window.location.assign(`${root}checkout`);
        return;
      }

      for (const row of this.rows) {
        const input = row.querySelector('[data-quantity-input]');
        if (input) input.value = '0';
      }

      if (this.message) this.message.textContent = `${totalQuantity} item${totalQuantity === 1 ? '' : 's'} added to cart.`;

      requestAnimationFrame(() => {
        if (matchMedia('(max-width: 749px)').matches && document.querySelector('[data-sk-pdp]')) return;
        const drawer = document.querySelector('theme-drawer#cart-drawer');
        drawer?.open?.();
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Could not add the selected sizes.';
      deferred.resolve({
        detail: {
          didError: true,
          source: 'fp-size-quantity',
          sourceId: this.id,
          itemCount: totalQuantity,
          productId: this.dataset.productId,
        },
      });

      this.dispatchEvent(new CartErrorEvent({
        error: message,
        code: 'INVALID',
        detail: { description: message },
      }));

      if (this.message) {
        this.message.textContent = message;
        this.message.classList.add('is-error');
      }
    } finally {
      this.setBusy(false, checkout);
      this.update();
    }
  }
}

if (!customElements.get('fp-size-quantity')) {
  customElements.define('fp-size-quantity', FpSizeQuantity);
}
